import { ITopicDetail, ITopicSection } from "@/api/topics";
import { Button } from "@/components/shared/buttons/Button";
import { Panel } from "@/components/shared/Panel";
import { Add, ExpandMore } from "@mui/icons-material";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Stack,
    Typography,
} from "@mui/material";
import { useDialogs } from "@toolpad/core/useDialogs";
import { AddClausesToTopicDialog } from "../dialogs/AddClausesToTopicDialog";
import { TopicClausesSection } from "./TopicClausesSection";

interface IProps {
    topic: ITopicDetail;
}

export const TopicSectionsPanel = ({ topic }: IProps) => {
    const dialogs = useDialogs();
    const sections = topic.clauses_by_section;

    const handleAddClauses = async () => {
        await dialogs.open(AddClausesToTopicDialog, { topicId: topic.id });
    };

    return (
        <Panel
            title="Sections"
            actions={
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Add fontSize="small" />}
                    onClick={handleAddClauses}
                    sx={{
                        color: (theme) => theme.palette.primary.contrastText,
                        borderColor: (theme) =>
                            theme.palette.primary.contrastText,
                        "&:hover": {
                            backgroundColor: (theme) =>
                                theme.palette.action.hover,
                        },
                        "& .MuiSvgIcon-root": {
                            fontSize: "1rem",
                        },
                    }}
                >
                    Add Clauses
                </Button>
            }
        >
            {sections.length === 0 ? (
                <Typography variant="body2" color="text.secondary" py={2}>
                    No clauses have been added to this topic yet.
                </Typography>
            ) : (
                <Stack spacing={2}>
                    {sections.map((section) => (
                        <SectionAccordion
                            key={
                                section.document_section_type?.id ??
                                "unassigned"
                            }
                            section={section}
                            topicId={topic.id}
                        />
                    ))}
                </Stack>
            )}
        </Panel>
    );
};

interface ISectionAccordionProps {
    section: ITopicSection;
    topicId: number;
    defaultExpanded?: boolean;
}

const SectionAccordion = ({
    section,
    topicId,
    defaultExpanded = true,
}: ISectionAccordionProps) => {
    const sectionName =
        section.document_section_type?.name ?? "Unassigned Section";
    const clauseCount = section.clauses.length;

    return (
        <Accordion
            defaultExpanded={defaultExpanded}
            disableGutters
            sx={{
                "&::before": {
                    display: "none",
                },
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{
                    bgcolor: (theme) => theme.palette.grey[100],
                    "&:hover": {
                        bgcolor: (theme) => theme.palette.grey[200],
                    },
                }}
            >
                <Stack direction="row" spacing={2} alignItems="center">
                    <Typography fontWeight={600}>{sectionName}</Typography>
                    {clauseCount > 0 && (
                        <Typography variant="body2" color="text.secondary">
                            {clauseCount} clause{clauseCount !== 1 ? "s" : ""}
                        </Typography>
                    )}
                </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 2 }}>
                <TopicClausesSection topicId={topicId} section={section} />
            </AccordionDetails>
        </Accordion>
    );
};
